import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import { noop } from 'lodash';
import {
    DatiqGraphSeriesType,
    DatiqGraphTooltipTypes,
    IDatiqGraph,
    IDatiqGraphSeries,
    IDatiqGraphSeriesData,
    IDatiqGraphTooltip,
} from '../models';
import { datiqAmcharts5SetColor } from './models';

export class DatiqAmcharts5TooltipBuilder {
    private _tooltip: am5.Tooltip;
    private tooltipIndex = 0;
    private _maxLines = 6;
    private _settings: am5.ITooltipSettings;
    private _seriesData: IDatiqGraphSeries;
    private _initSettings: Partial<am5.ITooltipSettings> = {
        paddingBottom: 0,
        paddingTop: 0,
        paddingLeft: 0,
        paddingRight: 0,
        getFillFromSprite: false,
    };

    constructor(
        series: am5xy.XYSeries,
        root: am5.Root,
        seriesData: IDatiqGraphSeries,
        graph: IDatiqGraph
    ) {
        this._seriesData = seriesData;
        this._tooltip = !!seriesData.showTooltip
            ? series?.set(
                  'tooltip',
                  am5.Tooltip.new(root, {
                      ...this._initSettings,
                  })
              )
            : am5.Tooltip.new(root, {});
        const fillOpacity: number =
            seriesData?.type === DatiqGraphSeriesType.Scatter ? 0.8 : 1;
        this._tooltip.get('background').setAll({
            stroke: this.getBorderColor(seriesData, series),
            strokeWidth: 2,
            fill: datiqAmcharts5SetColor('#ffffff'),
            fillOpacity: fillOpacity,
        });

        this._tooltip.events.on(
            'globalpointermove',
            (evt: am5.ISpritePointerEvent) => {
                this.updateBarTooltipPosition(seriesData, evt);
            }
        );

        this._tooltip.set('paddingBottom', 0);
    }

    private getBorderColor(
        seriesData: IDatiqGraphSeries,
        series: am5xy.XYSeries
    ): am5.Color {
        const tooltip: IDatiqGraphTooltip =
            seriesData?.toolTip as IDatiqGraphTooltip;

        return !!tooltip?.border?.length
            ? datiqAmcharts5SetColor(tooltip.border)
            : series?.get('stroke');
    }

    private updateBarTooltipPosition(
        seriesData: IDatiqGraphSeries,
        evt: am5.ISpritePointerEvent
    ) {
        if (seriesData.type !== DatiqGraphSeriesType.Bar) return;

        this.showBarTooltipDown(seriesData, evt)
            ? this._tooltip.set('pointerOrientation', 'down')
            : this.showBarTooltipUp(seriesData, evt)
            ? this._tooltip.set('pointerOrientation', 'up')
            : noop();
    }

    private showBarTooltipUp(
        seriesData: IDatiqGraphSeries,
        evt: am5.ISpritePointerEvent
    ) {
        return this.isBarTooltipShowing(seriesData) && this.getYValue(evt) < 0;
    }

    private showBarTooltipDown(
        seriesData: IDatiqGraphSeries,
        evt: am5.ISpritePointerEvent
    ) {
        return this.isBarTooltipShowing(seriesData) && this.getYValue(evt) > 0;
    }

    private getYValue(evt: am5.ISpritePointerEvent): number {
        const seriesData: IDatiqGraphSeriesData = evt?.target?.dataItem
            ?.dataContext as IDatiqGraphSeriesData;

        return (seriesData?.y as number) ?? 0;
    }

    private isBarTooltipShowing(seriesData: IDatiqGraphSeries) {
        return (
            this._tooltip.isShowing() &&
            seriesData.type === DatiqGraphSeriesType.Bar
        );
    }

    setLabel(graph: IDatiqGraph, seriesData: IDatiqGraphSeries): this {
        const labelHTML: string = this.isSingle(graph)
            ? this.setUpSingleToolTip(graph)
            : this.isMultiple(graph)
            ? this.setUpMultipleToolTip(graph)
            : this.isTable(graph)
            ? this.setUpTableToolTip(graph)
            : this.isMultipleMultipleTooltips(graph)
            ? this.setUpMultipleMultiplesTooltip(graph, seriesData.data)
            : this.setUpSingleToolTip(graph);

        this._settings = {
            ...this._settings,
            labelHTML,
        };

        return this;
    }

    setUpTableToolTip(graph: IDatiqGraph): string {
        return `<div class="datiq-graph-tooltip datiq-graph-tooltip--table">
        <div class="datiq-graph-tooltip__container">
            <p class="datiq-graph-tooltip__label datiq-graph-tooltip__title">{tooltipTable.title}</p>
            <section style="grid-template-columns: repeat(${
                graph.tooltipTableColumns + 1
            }, auto)" class="datiq-graph-tooltip--table__body">
                ${this.setUpTableTooltipHeaders(
                    graph.tooltipTableColumns ?? this._maxLines
                )}
                ${this.setUpTableTooltipRows(
                    graph.tooltipTableColumns ?? this._maxLines
                )}
            </section>
        </div>
    </div>`;
    }
    setUpTableTooltipRows(columns: number) {
        return [...new Array(this._maxLines)]
            .map((val: unknown, row: number): string => {
                return [...new Array(columns)].reduce(
                    (rowHtml: string, currentVal: unknown, column: number) => {
                        return `
                        ${rowHtml}
                    <p  class="datiq-graph-tooltip--table__col__info">
                      {tooltipTable.rows.${row}.columns.${column}.info}
                    </p>

                `;
                    },
                    ''
                );
            })
            .reduce(
                (template: string, currentTemplate: string, row: number) =>
                    `${template}<p class="datiq-graph-tooltip--table__col__label">  {tooltipTable.rows.${row}.label}</p>${currentTemplate}`,
                ``
            );
    }
    setUpTableTooltipHeaders(columns: number) {
        return [...new Array(columns)]
            .map(
                (val: unknown, index: number): string => `

        <p style="color:{tooltipTable.headers.${index}.color}" class="datiq-graph-tooltip--table__row__header">
          {tooltipTable.headers.${index}.label}
        </p>

    `
            )
            .reduce(
                (template: string, currentTemplate: string) =>
                    `${template}${currentTemplate}`,
                ` <p  class="datiq-graph-tooltip--table__row__header"></p>`
            );
    }
    setUpMultipleMultiplesTooltip(
        graph: IDatiqGraph,
        data: IDatiqGraphSeriesData[]
    ): string {
        if (data?.length === 0) return ``;
        return `<div class="datiq-graph-tooltip">
        <div class="datiq-graph-tooltip__container datiq-graph-tooltip--multiple-multiples__body">
        ${this.createMultipleMultiples(graph, data)}
        </div>
        </div>`;
    }

    createMultipleMultiples(
        graph: IDatiqGraph,
        data: IDatiqGraphSeriesData[]
    ): string {
        const num = data[0]?.tooltipMultipleMultiples?.length ?? 0;
        return [...new Array(num)]
            .map((val: unknown, index: number): string => {
                return `<span class="datiq-graph-tooltip--multiple" style="display:{tooltipMultipleMultiples.${index}.display}">
                        <p class="datiq-graph-tooltip__label datiq-graph-tooltip__title" style="color: {tooltipMultipleMultiples.${index}.titleColor}">
                        {tooltipMultipleMultiples.${index}.title}
                        </p>
                        <p class="datiq-graph-tooltip__label datiq-graph-tooltip__subTitle">{tooltipMultipleMultiples.${index}.subtitle}</p>
                        <section class="datiq-graph-tooltip--multiple__body">
                            ${this.insertTooltipMultiple(graph, index)}
                        </section>
                </span>`;
            })
            .reduce(
                (template: string, currentTemplate: string) =>
                    `${template}${currentTemplate}`,
                ''
            );
    }
    insertTooltipMultiple(graph, seriesIndex: number): string {
        return [...new Array(this._maxLines)]
            .map(
                (val: unknown, index: number): string => `
            <span style="display: flex; flex-direction: row;">
            <p
                style="color:{tooltipMultipleMultiples.${seriesIndex}.rows.${index}.color}; display: {tooltipMultipleMultiples.${seriesIndex}.rows.${index}.label.length > 0 ? 'inherit' : 'none'}"
                class="datiq-graph-tooltip--multiple__row__label"
            >
                {tooltipMultipleMultiples.${seriesIndex}.rows.${index}.label}
              </p>

            <p class="datiq-graph-tooltip--multiple__row__info">
              {tooltipMultipleMultiples.${seriesIndex}.rows.${index}.info}
            </p>
            </span>

        `
            )
            .reduce(
                (template: string, currentTemplate: string) =>
                    `${template}${currentTemplate}`,
                ''
            );
    }
    setUpMultipleToolTip(graph: IDatiqGraph): string {
        return `<div class="datiq-graph-tooltip datiq-graph-tooltip--multiple">
        <div class="datiq-graph-tooltip__container">
            <p class="datiq-graph-tooltip__label datiq-graph-tooltip__title" style="color: {tooltipMultiple.titleColor}">
            {tooltipMultiple.title}
            </p>
            <p class="datiq-graph-tooltip__label datiq-graph-tooltip__subTitle">{tooltipMultiple.subtitle}</p>
            <section class="datiq-graph-tooltip--multiple__body">
                ${this.setUpMultipleToolTipInfo(graph)}
            </section>
        </div>
    </div>`;
    }
    setUpMultipleToolTipInfo(graph: IDatiqGraph) {
        return [...new Array(this._maxLines)]
            .map(
                (val: unknown, index: number): string => `
            <span style="display: flex; flex-direction: row;">
            <p
                style="color:{tooltipMultiple.rows.${index}.color}; display: {tooltipMultiple.rows.${index}.label.length > 0 ? 'inherit' : 'none'}"
                class="datiq-graph-tooltip--multiple__row__label"
            >
                {tooltipMultiple.rows.${index}.label}
              </p>


            <p class="datiq-graph-tooltip--multiple__row__info" style="display: {tooltipMultiple.rows.${index}.notStrong.toString() === 'false' ? 'inherit' : 'none'}">
              {tooltipMultiple.rows.${index}.info}
            </p>
            </span>

        `
            )
            .reduce(
                (template: string, currentTemplate: string) =>
                    `${template}${currentTemplate}`,
                ''
            );
    }

    isSingle(graph: IDatiqGraph) {
        return graph?.tooltip?.type === DatiqGraphTooltipTypes.Single;
    }
    isMultiple(graph: IDatiqGraph) {
        return graph?.tooltip?.type === DatiqGraphTooltipTypes.Multiple;
    }

    isTable(graph: IDatiqGraph) {
        return graph?.tooltip?.type === DatiqGraphTooltipTypes.Table;
    }

    isMultipleMultipleTooltips(graph: IDatiqGraph) {
        return (
            graph?.tooltip?.type === DatiqGraphTooltipTypes.MultipleMultiples
        );
    }

    setUpSingleToolTip(graph: IDatiqGraph): string {
        return `<div class="datiq-graph-tooltip datiq-graph-tooltip--single">
                    <div class="datiq-graph-tooltip__container">
                        <p class="datiq-graph-tooltip__label datiq-graph-tooltip__title">{tooltipSingle.title}</p>
                        <p class="datiq-graph-tooltip__label datiq-graph-tooltip__info">{tooltipSingle.info}</p>
                    </div>
                </div>`;
    }

    build(): am5.Tooltip {
        this._tooltip.setAll(this._settings);

        return this._tooltip;
    }
}
